<script lang="ts" setup>
import { ref, computed, Ref, onBeforeMount, onBeforeUnmount, inject } from 'vue';
import { useEvents } from '@/plugins/events';
import { useLocalization } from '@/plugins/localization';
import { FormEntry } from '../form';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { CycleEntry, CycleType, CycleEntryData, instanceOfCycleEntry } from '.';
import properties from '../../properties';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { DateTime } from 'luxon';

defineOptions({
    name: 'cycle-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $events } = useEvents();
const { $t } = useLocalization();
const level = inject<number>('level', 0);

const entryData = ref(new CycleEntry()) as Ref<CycleEntry>;

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfCycleEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();
    }
});

const blueprint = computed(() => props.blueprint);

const value = computed({
    get()
    {
        const data = entryData.value.data;

        return data;
    },
    set(value: CycleEntryData)
    {
        entryData.value.data = value;
    },
});

const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));

// Cycle configuration computed properties
const startDate = computed({
    get()
    {
        return value.value?.startDate || null;
    },
    set(date: DateTime | null)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.startDate = date;
    }
});

const endType = computed({
    get()
    {
        return value.value?.endType || 'None';
    },
    set(type: string)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.endType = type as any;
    }
});

const endDate = computed({
    get()
    {
        return value.value?.endDate || null;
    },
    set(date: DateTime | null)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.endDate = date;
    }
});

const occurrences = computed({
    get()
    {
        return value.value?.occurences || null;
    },
    set(count: number | null)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.occurences = count;
    }
});

const frequencyType = computed({
    get()
    {
        return value.value?.frequencyType || 'Daily';
    },
    set(type: string)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.frequencyType = type as any;
    }
});

// Daily cycle settings
const dailyCycleSetting = computed({
    get()
    {
        return value.value?.dailyCycleSetting || { type: 'EveryNDays', interval: 1 };
    },
    set(setting: any)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.dailyCycleSetting = setting;
    }
});

// Weekly cycle settings
const weeklyCycleSetting = computed({
    get()
    {
        return value.value?.weeklyCycleSetting || { daysOfWeek: [], interval: 1 };
    },
    set(setting: any)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.weeklyCycleSetting = setting;
    }
});

// Monthly cycle settings
const monthlyCycleSetting = computed({
    get()
    {
        return value.value?.monthlyCycleSetting || { type: 'SetDayNumber', dayOfMonth: 1, interval: 1 };
    },
    set(setting: any)
    {
        if (!value.value) value.value = new CycleEntryData();

        value.value.monthlyCycleSetting = setting;
    }
});

// Day of week options
const dayOfWeekOptions = [
    { value: "Sunday", text: $t('[[[Niedziela]]]') },
    { value: "Monday", text: $t('[[[Poniedziałek]]]') },
    { value: "Tuesday", text: $t('[[[Wtorek]]]') },
    { value: "Wednesday", text: $t('[[[Środa]]]') },
    { value: "Thursday", text: $t('[[[Czwartek]]]') },
    { value: "Friday", text: $t('[[[Piątek]]]') },
    { value: "Saturday", text: $t('[[[Sobota]]]') }
];

// Week number options
const weekNumberOptions = [
    { value: 1, text: $t('[[[Pierwszy]]]') },
    { value: 2, text: $t('[[[Drugi]]]') },
    { value: 3, text: $t('[[[Trzeci]]]') },
    { value: 4, text: $t('[[[Czwarty]]]') },
    { value: 5, text: $t('[[[Ostatni]]]') }
];

// Time fields computed properties
const startTime = computed({
    get()
    {
        if (!entryData.value.data?.startTime) return null;

        // Return HH:mm format for HTML5 time input
        const timeStr = entryData.value.data.startTime.toString();

        // Extract just HH:mm from any format
        const timeParts = timeStr.split(':');

        if (timeParts.length >= 2)
        {
            return `${timeParts[0]}:${timeParts[1]}`;
        }

        return timeStr;
    },
    set(time: string | null)
    {
        if (!entryData.value.data) entryData.value.data = new CycleEntryData();

        if (time)
        {
            // Store as HH:mm format internally
            entryData.value.data.startTime = time as any;
        }
        else
        {
            entryData.value.data.startTime = null;
        }
    }
});

const endTime = computed({
    get()
    {
        if (!entryData.value.data?.endTime) return null;

        // Return HH:mm format for HTML5 time input
        const timeStr = entryData.value.data.endTime.toString();

        // Extract just HH:mm from any format
        const timeParts = timeStr.split(':');

        if (timeParts.length >= 2)
        {
            return `${timeParts[0]}:${timeParts[1]}`;
        }

        return timeStr;
    },
    set(time: string | null)
    {
        if (!entryData.value.data) entryData.value.data = new CycleEntryData();

        if (time)
        {
            // Store as HH:mm format internally
            entryData.value.data.endTime = time as any;
        }
        else
        {
            entryData.value.data.endTime = null;
        }
    }
});


// Initialize default data
if (!value.value)
{
    value.value = new CycleEntryData();
}
</script>

<template>
    <form-component-wrapper class="cycle-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />

                <!-- Start Date -->
                <field-datetime
                    v-model="startDate"
                    :label="$t('[[[Data rozpoczęcia]]]')"
                    type="date"
                />
                <form-error-message :entry="entryData" name="value.startDate" />

                <!-- Start Time -->
                <field-time
                    v-model="startTime"
                    :label="$t('[[[Godzina rozpoczęcia]]]')"
                />
                <form-error-message :entry="entryData" name="value.startTime" />

                <!-- End Time -->
                <field-time
                    v-model="endTime"
                    :label="$t('[[[Godzina zakończenia]]]')"
                />
                <form-error-message :entry="entryData" name="value.endTime" />

                <!-- End Type Configuration -->
                <div class="mb-3">
                    <label class="form-label">{{ $t('[[[Zakończenie cyklu]]]') }}</label>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-end-none`"
                            v-model="endType"
                            value="None"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-end-none`">
                            {{ $t('[[[Bez zakończenia]]]') }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-end-date`"
                            v-model="endType"
                            value="AfterDate"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-end-date`">
                            {{ $t('[[[Po dacie]]]') }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-end-occurrences`"
                            v-model="endType"
                            value="AfterNOcurrences"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-end-occurrences`">
                            {{ $t('[[[Po liczbie wystąpień]]]') }}
                        </label>
                    </div>
                </div>

                <!-- End Date Field -->
                <field-datetime
                    v-if="endType === 'AfterDate'"
                    v-model="endDate"
                    :label="$t('[[[Data zakończenia]]]')"
                    type="date"
                />
                <form-error-message :entry="entryData" name="value.endDate" />

                <!-- Occurrences Field -->
                <field-numeric
                    v-if="endType === 'AfterNOcurrences'"
                    v-model="occurrences"
                    :label="$t('[[[Liczba wystąpień]]]')"
                    :min="1"
                    :naturals="true"
                />
                <form-error-message :entry="entryData" name="value.occurrences" />

                <!-- Frequency Type Configuration -->
                <div class="mb-3">
                    <label class="form-label">{{ $t('[[[Częstotliwość]]]') }}</label>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-freq-daily`"
                            v-model="frequencyType"
                            value="Daily"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-freq-daily`">
                            {{ $t('[[[Codziennie]]]') }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-freq-weekly`"
                            v-model="frequencyType"
                            value="Weekly"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-freq-weekly`">
                            {{ $t('[[[Co tydzień]]]') }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-freq-monthly`"
                            v-model="frequencyType"
                            value="Monthly"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-freq-monthly`">
                            {{ $t('[[[Co miesiąc]]]') }}
                        </label>
                    </div>
                </div>

                <!-- Daily Cycle Settings -->
                <div v-if="frequencyType === 'Daily'" class="mb-3">
                    <label class="form-label">{{ $t('[[[Ustawienia dzienne]]]') }}</label>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-daily-every-n`"
                            v-model="dailyCycleSetting.type"
                            value="EveryNDays"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-daily-every-n`">
                            {{ $t('[[[Co N dni]]]') }}
                        </label>
                    </div>
                    <field-numeric
                        v-if="dailyCycleSetting.type === 'EveryNDays'"
                        v-model="dailyCycleSetting.interval"
                        :min="1"
                        :max="365"
                        :naturals="true"
                        class="mt-2"
                    />
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-daily-workday`"
                            v-model="dailyCycleSetting.type"
                            value="EveryWorkDay"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-daily-workday`">
                            {{ $t('[[[Każdy dzień roboczy]]]') }}
                        </label>
                    </div>
                    <form-error-message :entry="entryData" name="value.dailyCycleSetting.interval" />
                </div>

                <!-- Weekly Cycle Settings -->
                <div v-if="frequencyType === 'Weekly'" class="mb-3">
                    <div class="mb-2">
                        <label class="form-label">{{ $t('[[[Dni tygodnia]]]') }}</label>
                        <div class="row">
                            <div class="col-6" v-for="day in dayOfWeekOptions" :key="day.value">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        :id="`${blueprint.id}-day-${day.value}`"
                                        :value="day.value"
                                        v-model="weeklyCycleSetting.daysOfWeek"
                                        :disabled="readonly"
                                    />
                                    <label class="form-check-label" :for="`${blueprint.id}-day-${day.value}`">
                                        {{ day.text }}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <form-error-message :entry="entryData" name="value.weeklyCycleSetting.daysOfWeek" />
                    </div>
                    <field-numeric
                        v-model="weeklyCycleSetting.interval"
                        :label="$t('[[[Co ile tygodni]]]')"
                        :min="1"
                        :max="52"
                        :naturals="true"
                    />
                    <form-error-message :entry="entryData" name="value.weeklyCycleSetting.interval" />
                </div>

                <!-- Monthly Cycle Settings -->
                <div v-if="frequencyType === 'Monthly'" class="mb-3">
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-monthly-day-number`"
                            v-model="monthlyCycleSetting.type"
                            value="SetDayNumber"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-monthly-day-number`">
                            {{ $t('[[[Dzień miesiąca]]]') }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="`${blueprint.id}-monthly-day-week`"
                            v-model="monthlyCycleSetting.type"
                            value="SetDayOfWeek"
                            :disabled="readonly"
                        />
                        <label class="form-check-label" :for="`${blueprint.id}-monthly-day-week`">
                            {{ $t('[[[Dzień tygodnia w miesiącu]]]') }}
                        </label>
                    </div>

                    <field-numeric
                        v-if="monthlyCycleSetting.type === 'SetDayNumber'"
                        v-model="monthlyCycleSetting.dayOfMonth"
                        :label="$t('[[[Dzień miesiąca]]]')"
                        :min="1"
                        :max="31"
                        :naturals="true"
                        class="mt-2 mb-2"
                    />
                    <form-error-message :entry="entryData" name="value.monthlyCycleSetting.dayOfMonth" />

                    <div v-if="monthlyCycleSetting.type === 'SetDayOfWeek'" class="mt-2 mb-2">
                        <div class="row">
                            <div class="col-6">
                                <label class="form-label">{{ $t('[[[Dzień tygodnia]]]') }}</label>
                                <select
                                    class="form-select"
                                    v-model="monthlyCycleSetting.dayOfWeek"
                                    :disabled="readonly"
                                    :required="monthlyCycleSetting.type === 'SetDayOfWeek'"
                                >
                                    <option v-for="day in dayOfWeekOptions" :key="day.value" :value="day.value">
                                        {{ day.text }}
                                    </option>
                                </select>
                                <form-error-message :entry="entryData" name="value.monthlyCycleSetting.dayOfWeek" />
                            </div>
                            <div class="col-6">
                                <label class="form-label">{{ $t('[[[Który w miesiącu]]]') }}</label>
                                <select
                                    class="form-select"
                                    v-model="monthlyCycleSetting.weekNumber"
                                    :disabled="readonly"
                                    :required="monthlyCycleSetting.type === 'SetDayOfWeek'"
                                >
                                    <option v-for="week in weekNumberOptions" :key="week.value" :value="week.value">
                                        {{ week.text }}
                                    </option>
                                </select>
                                <form-error-message :entry="entryData" name="value.monthlyCycleSetting.weekNumber" />
                            </div>
                        </div>
                    </div>

                    <field-numeric
                        v-model="monthlyCycleSetting.interval"
                        :label="$t('[[[Co ile miesięcy]]]')"
                        :min="1"
                        :max="12"
                        :naturals="true"
                    />
                    <form-error-message :entry="entryData" name="value.monthlyCycleSetting.interval" />
                </div>

                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <ideo-form-localize v-slot="{ locale }">
                <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
            </ideo-form-localize>
            <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
            <ideo-form-localize v-slot="{ locale }">
                <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
            </ideo-form-localize>
            <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
            <field-readonly :form="form" :blueprint="blueprint" />
            <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
        </template>
    </form-component-wrapper>
</template>
